<script lang="ts" setup>
import { useSchemaOrg, defineWebPage } from '@unhead/schema-org/vue';
import { useSdk } from '~/sdk';
import { orderBy, groupBy } from 'lodash-es';

const route = useRoute();
const localePath = useLocalePath();

const { data, refresh } = await useAsyncData(`brands:${route.query.page}`, async () => {
  return useSdk().commerce.getBrands({
    page: (route.query.page as string) || 1,
  });
});

useSeoMeta({
  title: data.value?.meta_title,
  description: data.value?.meta_description,
  ogTitle: data.value?.meta_title,
  ogDescription: data.value?.meta_description,
});

useSchemaOrg([
  defineWebPage({
    title: data.value?.meta_title,
    description: data.value?.meta_description,
  }),
]);

const paginationLinks = computed(() => {
  const links = [];
  const currentPage = data.value?.pagination?.current_page || 1;
  const totalPages = data.value?.pagination?.pages_count || 1;
  const baseUrl = localePath('/brands');

  if (currentPage > 1) {
    const prevPage = currentPage - 1;
    const prevUrl = prevPage === 1 ? baseUrl : `${baseUrl}?page=${prevPage}`;
    links.push({
      rel: 'prev',
      href: prevUrl,
    });
  }

  if (currentPage < totalPages) {
    const nextPage = currentPage + 1;
    links.push({
      rel: 'next',
      href: `${baseUrl}?page=${nextPage}`,
    });
  }

  return links;
});

useHead(() => ({
  link: paginationLinks.value,
}));

watch(
  () => route.query.page,
  () => refresh(),
);
</script>

<template>
  <VContainer v-if="data">
    <div v-if="data">
      <h1 class="mt-8 mb-4 font-bold text-2xl border-b pb-1">{{ data.name }}</h1>
      <div class="prose max-w-none mb-8" v-html="data.description"></div>
    </div>

    <div class="flex flex-col gap-4">
      <div v-for="(brands, letter) in groupBy(orderBy(data.results, 'name', 'asc'), (item) => item.name.charAt(0))">
        <h2 class="text-lg text-left font-semibold mb-2">{{ letter }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <NuxtLink v-for="brand in brands" :to="localePath(brand.link)" class="p-4 bg-gray-100 hover:bg-gray-200 rounded">
            <h2>{{ brand.name }}</h2>
          </NuxtLink>
        </div>
      </div>
    </div>
  </VContainer>
</template>
